import React, { useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  TextField,
  Divider,
} from '@mui/material';
import {
  assemblePatientLibrarySection54,
  CHILD_BEARING_PATHWAY,
  GENOTOXICITY_RISK,
  FEMALE_REPRODUCTIVE_RISK,
  MALE_EJACULATE_RISK,
  DEFAULT_PATIENT_LIBRARY_DECISIONS,
} from '../../utils/patientLibrarySection54';

const DECISIONS_PATH = 'template_language_fields.patient_library_decisions';

const PatientLibrarySection54Panel = ({
  values,
  setFieldValue,
  isFormDisabled,
}) => {
  const decisions = values?.template_language_fields?.patient_library_decisions || DEFAULT_PATIENT_LIBRARY_DECISIONS;
  const pathway = decisions.childbearing_population_pathway || '';

  const setDecision = useCallback(
    (field, value) => {
      setFieldValue(`${DECISIONS_PATH}.${field}`, value);
    },
    [setFieldValue]
  );

  const reproductiveStatus = values?.target_population?.reproductive_status_contraception || '';

  const assemblyInput = useMemo(
    () => ({
      template_language_fields: {
        patient_library_decisions: decisions,
      },
      target_population: {
        reproductive_status_contraception: reproductiveStatus,
      },
    }),
    [
      pathway,
      decisions.genotoxicity_risk,
      decisions.female_reproductive_risk,
      decisions.male_ejaculate_risk,
      decisions.has_study_specific_reproductive_requirements,
      decisions.study_specific_reproductive_requirements,
      reproductiveStatus,
    ]
  );

  const assembly = useMemo(() => assemblePatientLibrarySection54(assemblyInput), [assemblyInput]);

  const syncTemplateFields = useCallback(() => {
    setFieldValue(
      'template_language_fields.definitions_childbearing_potential',
      assembly.definitions_childbearing_potential
    );
    setFieldValue(
      'template_language_fields.contraception_requirements',
      assembly.contraception_requirements
    );
  }, [assembly, setFieldValue]);

  useEffect(() => {
    syncTemplateFields();
  }, [syncTemplateFields]);

  const showCbPQuestions = pathway === CHILD_BEARING_PATHWAY.CBP;
  const showReproductiveRiskQuestions =
    showCbPQuestions && decisions.genotoxicity_risk === GENOTOXICITY_RISK.NO;

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid #dbe3eb', borderRadius: 1, bgcolor: '#fafbfc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        TransCelerate Patient Library — Sections 5.4.1 &amp; 5.4.2
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the participant population and contraception decision tree answers. Template language
        below is assembled verbatim from the Patient Library (no AI rewriting).
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          Section 5.4.1 — Participant Population
        </FormLabel>
        <RadioGroup
          value={pathway}
          onChange={(e) => {
            const next = e.target.value;
            setDecision('childbearing_population_pathway', next);
            if (next === CHILD_BEARING_PATHWAY.NCBP_ONLY) {
              setDecision('genotoxicity_risk', '');
              setDecision('female_reproductive_risk', '');
              setDecision('male_ejaculate_risk', '');
              setDecision('has_study_specific_reproductive_requirements', false);
              setDecision('study_specific_reproductive_requirements', '');
            }
          }}
        >
          <FormControlLabel
            value={CHILD_BEARING_PATHWAY.CBP}
            control={<Radio size="small" />}
            label="Participants of Childbearing Potential (CBP)"
          />
          <FormControlLabel
            value={CHILD_BEARING_PATHWAY.NCBP_ONLY}
            control={<Radio size="small" />}
            label="Only Participants of Nonchildbearing Potential (NCBP)"
          />
        </RadioGroup>
      </FormControl>

      {pathway === CHILD_BEARING_PATHWAY.NCBP_ONLY && (
        <Alert severity="info" sx={{ mb: 2 }}>
          For NCBP-only studies, Section 5.4.2 will state that contraception requirements are not
          applicable. Documented reproductive safety requirements from intake will be appended if
          present.
        </Alert>
      )}

      {showCbPQuestions && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Section 5.4.2 — Contraception Decision Tree
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5 }}>
              1. Is there a risk of clinically relevant genotoxicity associated with the investigational product?
            </FormLabel>
            <RadioGroup
              value={decisions.genotoxicity_risk || ''}
              onChange={(e) => {
                const next = e.target.value;
                setDecision('genotoxicity_risk', next);
                if (next === GENOTOXICITY_RISK.YES) {
                  setDecision('female_reproductive_risk', '');
                  setDecision('male_ejaculate_risk', '');
                }
              }}
            >
              <FormControlLabel value={GENOTOXICITY_RISK.YES} control={<Radio size="small" />} label="Yes (Female F1A + Male M1A)" />
              <FormControlLabel value={GENOTOXICITY_RISK.NO} control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </FormControl>

          {showReproductiveRiskQuestions && (
            <>
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5 }}>
                  2a. Female Participants — reproductive risk classification
                </FormLabel>
                <RadioGroup
                  value={decisions.female_reproductive_risk || ''}
                  onChange={(e) => setDecision('female_reproductive_risk', e.target.value)}
                >
                  <FormControlLabel
                    value={FEMALE_REPRODUCTIVE_RISK.DEMONSTRATED_SUSPECTED}
                    control={<Radio size="small" />}
                    label="Demonstrated or Suspected Human Teratogenicity/Fetotoxicity (F1B)"
                  />
                  <FormControlLabel
                    value={FEMALE_REPRODUCTIVE_RISK.POSSIBLE}
                    control={<Radio size="small" />}
                    label="Possible Human Teratogenicity/Fetotoxicity (F2)"
                  />
                  <FormControlLabel
                    value={FEMALE_REPRODUCTIVE_RISK.UNLIKELY}
                    control={<Radio size="small" />}
                    label="Unlikely Human Teratogenicity/Fetotoxicity (F3)"
                  />
                </RadioGroup>
              </FormControl>

              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5 }}>
                  2b. Male Participants — ejaculate risk
                </FormLabel>
                <RadioGroup
                  value={decisions.male_ejaculate_risk || ''}
                  onChange={(e) => setDecision('male_ejaculate_risk', e.target.value)}
                >
                  <FormControlLabel
                    value={MALE_EJACULATE_RISK.RISK_VIA_EJACULATE}
                    control={<Radio size="small" />}
                    label="Risk via Ejaculate (M1B)"
                  />
                  <FormControlLabel
                    value={MALE_EJACULATE_RISK.NO_RISK_VIA_EJACULATE}
                    control={<Radio size="small" />}
                    label="No Risk via Ejaculate (no male Patient Library block)"
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}

          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5 }}>
              3. Does the protocol include additional study-specific reproductive safety requirements?
            </FormLabel>
            <RadioGroup
              value={decisions.has_study_specific_reproductive_requirements ? 'yes' : 'no'}
              onChange={(e) => {
                const yes = e.target.value === 'yes';
                setDecision('has_study_specific_reproductive_requirements', yes);
                if (!yes) {
                  setDecision('study_specific_reproductive_requirements', '');
                }
              }}
            >
              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </FormControl>

          {decisions.has_study_specific_reproductive_requirements && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              label="Study-specific reproductive safety requirements (appended after Patient Library content)"
              value={decisions.study_specific_reproductive_requirements || ''}
              onChange={(e) =>
                setDecision('study_specific_reproductive_requirements', e.target.value)
              }
              disabled={isFormDisabled}
              placeholder="e.g., contraception duration after last dose, pregnancy testing schedule, donation restrictions…"
              sx={{ mb: 2 }}
            />
          )}
        </>
      )}

      {assembly.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {assembly.warnings.map((w) => (
            <Typography key={w} variant="body2" component="div">
              {w}
            </Typography>
          ))}
        </Alert>
      )}

      {assembly.complete && (
        <Alert severity="success" sx={{ mb: 0 }}>
          Patient Library sections 5.4.1 and 5.4.2 are assembled from your selections and will be
          used verbatim in the protocol (no manual text entry required).
        </Alert>
      )}
    </Box>
  );
};

export default PatientLibrarySection54Panel;
